import React, { useMemo } from 'react';
import { Control, UseFormWatch, useFieldArray } from 'react-hook-form';

import { RHFInputText } from '../../../components/rhf/RHFInputText';
import { RHFInputSelect } from '../../../components/rhf/RHFInputSelect';
import { RHFInputDate } from '../../../components/rhf/RHFInputDate';
import { RHFInputCheckbox } from '../../../components/rhf/RHFInputCheckbox';
import { RHFSearchableSelect } from '../../../components/rhf/RHFSearchableSelect';
import { PERMIT_TYPES, GENDERS, CIVIL_STATUSES } from '../../../schemas/employee.schema';
import { EmployeeFormData } from '../../../schemas/employee.schema';
import { getCantonOptions } from '../../../data/cantons';
import { getCountryOptions } from '../../../data/country-options';

interface EmployeeFormFieldsProps {
  control: Control<EmployeeFormData>;
  watch: UseFormWatch<EmployeeFormData>;
  lang: any;
  isEdit?: boolean;
}

const AVS_PLACEHOLDER = '756.____.____.__ ';

const EmployeeFormFields: React.FC<EmployeeFormFieldsProps> = ({ control, watch, lang, isEdit }) => {
  const civilStatus = watch('civil_status');
  const permitType = watch('permit.type');
  const residenceCountry = watch('residence.country');
  const hasSpouse = watch('spouse.has_spouse');
  const spouseIsWorking = watch('spouse.is_working');

  const showCivilStatusSince = civilStatus && civilStatus !== 'single';

  const { fields: childFields, append: appendChild, remove: removeChild } = useFieldArray({
    control,
    name: 'children',
  });

  const langCode = lang.code?.toLowerCase() || 'fr';
  const cantonOptions = useMemo(() => getCantonOptions(langCode), [langCode]);
  const countryOptions = useMemo(() => getCountryOptions(langCode), [langCode]);
  const genderOptions = GENDERS.map(g => ({ value: g, label: lang.labels[`gender_${g}`] || g }));
  const civilStatusOptions = CIVIL_STATUSES.map(s => ({ value: s, label: lang.labels[`civilStatus_${s}`] || s.replace(/_/g, ' ') }));
  const permitOptions = PERMIT_TYPES.map(p => ({ value: p, label: p === 'CH' ? lang.labels.swissNational || 'Swiss national' : `${lang.labels.permit || 'Permit'} ${p}` }));

  return (
    <>
      {/* Identity */}
      <h4 className="fw-bolder mb-5">{lang.labels.identity || 'Identity'}</h4>
      <div className="row">
        <div className="col-md-6">
          <RHFInputText name="avs_number" control={control} label={lang.labels.avsNumber} required placeholder={AVS_PLACEHOLDER} />
        </div>
        <div className="col-md-6">
          <RHFInputDate name="birth_date" control={control} label={lang.labels.birthDate || 'Date of birth'} required />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          <RHFInputSelect name="gender" control={control} label={lang.labels.gender || 'Gender'} options={genderOptions} required placeholder={lang.labels.select || 'Select...'} />
        </div>
        <div className={showCivilStatusSince ? 'col-md-4' : 'col-md-8'}>
          <RHFInputSelect name="civil_status" control={control} label={lang.labels.civilStatus || 'Civil status'} options={civilStatusOptions} required placeholder={lang.labels.select || 'Select...'} />
        </div>
        {showCivilStatusSince && (
          <div className="col-md-4">
            <RHFInputDate name="civil_status_since" control={control} label={lang.labels.civilStatusSince || 'Civil status since'} />
            <div className="form-text text-muted mt-1" style={{ fontSize: '0.85em' }}>
              {lang.labels.civilStatusSinceHelp || 'Determines the applicable withholding tax scale. A change of civil status takes effect the month following the event.'}
            </div>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-md-6">
          <RHFSearchableSelect name="nationality" control={control} label={lang.labels.nationality || 'Nationality'} options={countryOptions} required placeholder={lang.labels.select || 'Select...'} />
        </div>
        <div className="col-md-6">
          <RHFSearchableSelect name="work_canton" control={control} label={lang.labels.workCanton} options={cantonOptions} required placeholder={lang.labels.select || 'Select...'} />
        </div>
      </div>

      {/* Permit */}
      <div className="separator my-5"></div>
      <h4 className="fw-bolder mb-5">{lang.labels.residencePermit}</h4>
      <div className="row">
        <div className="col-md-4">
          <RHFInputSelect name="permit.type" control={control} label={lang.labels.permitType || 'Permit type'} options={permitOptions} required />
        </div>
        {permitType !== 'CH' && (
          <>
            <div className="col-md-4">
              <RHFInputText name="permit.number" control={control} label={lang.labels.permitNumber || 'Permit number'} />
            </div>
            <div className="col-md-4">
              <RHFInputDate name="permit.valid_until" control={control} label={lang.labels.permitValidUntil || 'Valid until'} required />
            </div>
            <div className="col-md-4">
              <RHFSearchableSelect name="permit.issuing_canton" control={control} label={lang.labels.issuingCanton || 'Issuing canton'} options={cantonOptions} placeholder={lang.labels.select || 'Select...'} />
            </div>
          </>
        )}
      </div>

      {/* Residence */}
      <div className="separator my-5"></div>
      <h4 className="fw-bolder mb-5">{lang.labels.residence || 'Residence'}</h4>
      <div className="row">
        <div className="col-md-4">
          <RHFSearchableSelect name="residence.country" control={control} label={lang.labels.country} options={countryOptions} required placeholder={lang.labels.select || 'Select...'} />
        </div>
        {residenceCountry === 'CH' && (
          <>
            <div className="col-md-4">
              <RHFSearchableSelect name="residence.canton" control={control} label={lang.labels.canton || 'Canton'} options={cantonOptions} required placeholder={lang.labels.select || 'Select...'} />
            </div>
            <div className="col-md-4">
              <RHFInputText name="residence.municipality" control={control} label={lang.labels.municipality || 'Municipality'} />
            </div>
          </>
        )}
      </div>

      {/* Spouse */}
      <div className="separator my-5"></div>
      <h4 className="fw-bolder mb-5">{lang.labels.familySituation || 'Family situation'}</h4>
      <RHFInputCheckbox name="spouse.has_spouse" control={control} label={lang.labels.hasSpouse || 'Has spouse / registered partner'} />
      {hasSpouse && (
        <div className="row">
          <div className="col-md-4">
            <RHFInputCheckbox name="spouse.is_working" control={control} label={lang.labels.spouseIsWorking || 'Spouse is working'} />
          </div>
          {spouseIsWorking && (
            <>
              <div className="col-md-4">
                <RHFSearchableSelect name="spouse.work_canton" control={control} label={lang.labels.spouseWorkCanton || 'Work canton'} options={cantonOptions} placeholder={lang.labels.select || 'Select...'} />
              </div>
              <div className="col-md-4">
                <RHFSearchableSelect name="spouse.work_country" control={control} label={lang.labels.spouseWorkCountry || 'Work country'} options={countryOptions} placeholder={lang.labels.select || 'Select...'} />
              </div>
            </>
          )}
        </div>
      )}

      {/* Children */}
      <div className="separator my-5"></div>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h4 className="fw-bolder mb-0">{lang.labels.children || 'Children'}</h4>
        <button
          type="button"
          className="btn btn-sm btn-light-primary"
          onClick={() => appendChild({ first_name: '', last_name: '', birth_date: '', allowance_from: '', allowance_to: '', in_education: false })}
        >
          <i className="bi bi-plus-lg me-1"></i>
          {lang.labels.addChild || 'Add child'}
        </button>
      </div>
      {childFields.map((field, index) => (
        <div key={field.id} className="card card-bordered mb-3 p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <span className="fw-bold fs-6">{`${lang.labels.child || 'Child'} ${index + 1}`}</span>
            <button type="button" className="btn btn-sm btn-light-danger" onClick={() => removeChild(index)}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
          <div className="row">
            <div className="col-md-4">
              <RHFInputText name={`children.${index}.first_name`} control={control} label={lang.labels.firstName} required />
            </div>
            <div className="col-md-4">
              <RHFInputText name={`children.${index}.last_name`} control={control} label={lang.labels.lastName} required />
            </div>
            <div className="col-md-4">
              <RHFInputDate name={`children.${index}.birth_date`} control={control} label={lang.labels.birthDate || 'Date of birth'} required />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <RHFInputDate name={`children.${index}.allowance_from`} control={control} label={lang.labels.allowanceFrom || 'Allowance from'} />
            </div>
            <div className="col-md-4">
              <RHFInputDate name={`children.${index}.allowance_to`} control={control} label={lang.labels.allowanceTo || 'Allowance to'} />
            </div>
            <div className="col-md-4">
              <RHFInputCheckbox name={`children.${index}.in_education`} control={control} label={lang.labels.inEducation || 'In education'} />
            </div>
          </div>
        </div>
      ))}

      {/* Payment */}
      <div className="separator my-5"></div>
      <h4 className="fw-bolder mb-5">{lang.labels.paymentDetails || 'Payment details'}</h4>
      <div className="row">
        <div className="col-md-4">
          <RHFInputText name="payment.iban" control={control} label={lang.labels.iban} />
        </div>
        <div className="col-md-4">
          <RHFInputText name="payment.bic" control={control} label={lang.labels.bic || 'BIC/SWIFT'} />
        </div>
        <div className="col-md-4">
          <RHFInputText name="payment.holder_name" control={control} label={lang.labels.accountHolder || 'Account holder'} />
        </div>
      </div>

      {/* Notes */}
      <div className="separator my-5"></div>
      <h4 className="fw-bolder mb-5">{lang.labels.notes || 'Notes'}</h4>
      <RHFInputText name="notes" control={control} label={lang.labels.notes || 'Notes'} />
    </>
  );
};

export default EmployeeFormFields;
